import { Group, Stack, Text } from "@mantine/core"
import { Card } from "@tfg/ui"
import React from "react"
import { Trans, useTranslation } from "react-i18next"

import { FindCertificateRequestsByCertificateIdItem } from "~/gen/api"
import { CertificateRequestStatusBadgeWithActions } from "~/pages/Brand/Certificates/components/CertificateRequestStatusBadgeWithActions"
import { formatDate } from "~/utils/date"
import { useGetOperations } from "~/utils/operations"

export type Props = {
  certificateRequest: FindCertificateRequestsByCertificateIdItem
}

export const CompletedCertificateRequestCard = ({
  certificateRequest,
}: Props) => {
  const {
    t,
    i18n: { language },
  } = useTranslation("certificates")

  const operations = useGetOperations()

  return (
    <Card key={certificateRequest.id} h="fit-content" mih="fit-content" my="xs">
      <Stack gap="xs">
        <Text fw={300} c="var(--mantine-color-dimmed)" ta="right">
          {t("modal.associatedRequests.card.header", {
            createdAt: formatDate(certificateRequest.createdAt, language),
          })}
        </Text>
        <Text>
          <Trans
            ns="certificates"
            i18nKey="modal.associatedRequests.card.description.common"
            components={{
              type: <React.Fragment />,
              maxIssueDate: <React.Fragment />,
              productionSiteName: <React.Fragment />,
              productionSiteOrganizationName: <React.Fragment />,
              activityName: <React.Fragment />,
            }}
            values={{
              type: certificateRequest.type.name,
              maxIssueDate: certificateRequest.maxIssueDate
                ? formatDate(certificateRequest.maxIssueDate, language)
                : "",
              productionSiteName: certificateRequest.productionSite.name ?? "",
              productionSiteOrganizationName:
                certificateRequest.productionSite.organization.name ?? "",
              activityName:
                operations[certificateRequest.activityName]?.name ??
                certificateRequest.activityName,
            }}
          />
        </Text>
        <Group>
          {certificateRequest.product ? (
            <Text>
              <Trans
                ns="certificates"
                i18nKey="modal.associatedRequests.card.description.product"
                components={{
                  productBrandReference: <React.Fragment />,
                  productColor: <React.Fragment />,
                }}
                values={{
                  productColor: certificateRequest.product.color,
                  productBrandReference:
                    certificateRequest.product.brandReference,
                }}
              />
            </Text>
          ) : null}
          {certificateRequest.purchaseOrder.product ? (
            <Text>
              <Trans
                ns="certificates"
                i18nKey="modal.associatedRequests.card.description.purchaseOrder"
                components={{
                  purchaseOrder: <React.Fragment />,
                }}
                values={{
                  purchaseOrder: certificateRequest.purchaseOrder.product,
                }}
              />
            </Text>
          ) : null}
        </Group>
        <Group>
          {certificateRequest.componentName ? (
            <Text>
              <Trans
                ns="certificates"
                i18nKey="modal.associatedRequests.card.description.component"
                values={{
                  componentName: certificateRequest.componentName,
                }}
                components={{
                  componentName: <React.Fragment />,
                }}
              />
            </Text>
          ) : null}
          {certificateRequest.materialName ? (
            <Text>
              <Trans
                ns="certificates"
                i18nKey="modal.associatedRequests.card.description.material"
                values={{
                  materialName: certificateRequest.materialName,
                }}
                components={{
                  materialName: <React.Fragment />,
                }}
              />
            </Text>
          ) : null}
          {(certificateRequest.componentName ||
            certificateRequest.materialName) &&
          certificateRequest.purchaseOrder.operation ? (
            <Text>
              <Trans
                ns="certificates"
                i18nKey="modal.associatedRequests.card.description.purchaseOrder"
                components={{
                  purchaseOrder: <React.Fragment />,
                }}
                values={{
                  purchaseOrder: certificateRequest.purchaseOrder.operation,
                }}
              />
            </Text>
          ) : null}
        </Group>
        <Stack>
          <CertificateRequestStatusBadgeWithActions
            certificateRequestStatus={certificateRequest.status}
            certificateRequestId={certificateRequest.id}
            certificateId={certificateRequest.certificateId}
            userType="brand"
          />
          {certificateRequest.rejectionMessage && (
            <Text c="var(--mantine-color-error-text)">
              {certificateRequest.rejectionMessage}
            </Text>
          )}
        </Stack>
      </Stack>
    </Card>
  )
}
